import { fetchApi } from '@/services/api-client';

export type CafeteriaPaymentMethod = 'wallet' | 'thai_qr';

export interface CafeteriaOrderSummary {
  id: string;
  createdAt: string;
  payerName: string | null;
  payerGrade: string | null;
  payerLabel: string | null;
  paymentMethod: CafeteriaPaymentMethod;
  total: number;
  refundedTotal: number;
  /** e.g. "Pad thai, Water ×2" */
  itemsSummary: string;
  cashierName: string | null;
  /** Same day as the sale (Bangkok) and something left to refund. */
  refundable: boolean;
}

export interface CafeteriaOrdersPage {
  rows: CafeteriaOrderSummary[];
  total: number;
  page: number;
  limit: number;
}

export interface CafeteriaOrderItem {
  id: string;
  name: string;
  unitPrice: number;
  quantity: number;
  lineTotal: number;
  refundedQuantity: number;
}

export interface CafeteriaRefund {
  id: string;
  createdAt: string;
  amount: number;
  comment: string;
  performedByName: string | null;
  items: { orderItemId: string; name: string; quantity: number; amount: number }[];
}

export interface CafeteriaOrderDetail extends CafeteriaOrderSummary {
  items: CafeteriaOrderItem[];
  refunds: CafeteriaRefund[];
}

export interface CafeteriaOrderFilters {
  /** Bangkok days, YYYY-MM-DD; the API defaults both to today. */
  from?: string;
  to?: string;
  paymentMethod?: CafeteriaPaymentMethod;
  refunded?: boolean;
  q?: string;
  page?: number;
  limit?: number;
}

export interface CreateCafeteriaRefund {
  items: { orderItemId: string; quantity: number }[];
  comment: string;
}

export interface CafeteriaRefundResult {
  refund: CafeteriaRefund;
  /** The wallet's new balance; null for a Thai QR sale. */
  balance: number | null;
}

const BASE = '/admin/cafeteria/orders';

export const cafeteriaOrdersApi = {
  list: (filters: CafeteriaOrderFilters) => {
    const params = new URLSearchParams();
    if (filters.from) params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);
    if (filters.paymentMethod) params.append('paymentMethod', filters.paymentMethod);
    if (filters.refunded) params.append('refunded', 'true');
    if (filters.q) params.append('q', filters.q);
    if (filters.page) params.append('page', String(filters.page));
    if (filters.limit) params.append('limit', String(filters.limit));
    const qs = params.toString();
    return fetchApi<CafeteriaOrdersPage>(`${BASE}${qs ? `?${qs}` : ''}`);
  },
  get: (id: string) => fetchApi<CafeteriaOrderDetail>(`${BASE}/${id}`),
  refund: (id: string, input: CreateCafeteriaRefund) =>
    fetchApi<CafeteriaRefundResult>(`${BASE}/${id}/refunds`, { method: 'POST', body: JSON.stringify(input) }),
};
